<template>
  <div class="flex flex-col items-center justify-center w-full bg-[#F2F6FA]">
    <section class="flex flex-col items-center w-full justify-center">
      <div class="relative w-full" style="height: calc(100vh - 100px)">
        <!-- Fixed Filter Bar -->
        <div
          class="absolute top-0 left-0 w-full bg-white p-4 shadow-md z-50 flex flex-wrap gap-4 items-center"
        >
          <div class="location-filter flex-grow md:max-w-xs">
            <div class="font-semibold text-sm mb-1">Location</div>
            <div class="relative">
              <v-text-field
                v-model="locationSearch"
                :label="
                  selectedStore
                    ? selectedStore.gmbProfile?.title || selectedStore.title
                    : 'Search locations...'
                "
                hide-details
                dense
                outlined
                @click="toggleStoreDropdown($event)"
                class="location-input rounded-lg"
                color="rgba(0, 0, 0, 0.6)"
                background-color="white"
                prepend-inner-icon="mdi-magnify"
              >
                <template v-slot:append>
                  <button
                    @click="toggleStoreDropdown($event)"
                    class="flex items-center px-2 transition-transform duration-200"
                    :class="{'rotate-180': showStoreDropdown}"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </template>
              </v-text-field>
              <div
                ref="showStoreDropdown"
                v-if="showStoreDropdown"
                class="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 max-h-60 min-w-[300px] max-w-[800px] overflow-y-auto py-2 px-2 transition-all duration-200"
              >
                <div
                  v-for="profile in filteredStores"
                  :key="profile.wid"
                  class="p-3 hover:bg-blue-50 rounded-md cursor-pointer transition-colors duration-150"
                  @click="selectStore(profile)"
                  :class="{ 'bg-blue-50 border-l-4 border-blue-500': selectedWID === profile.wid }"
                >
                  <div class="flex items-center gap-2">
                    <div class="flex flex-col">
                      <span class="text-md font-medium">{{
                        profile?.gmbProfile?.title || profile?.title
                      }}</span>
                      <span
                        class="text-sm"
                        v-if="profile.sub_locality || profile.display_name"
                        >{{
                          profile.shallowConnect
                            ? profile.address
                            : profile.sub_locality || profile.display_name
                        }}</span
                      >
                      <div class="text-sm text-gray-600 flex gap-1">
                        <span>{{ profile.city }}</span>
                        <span v-if="profile.state">· {{ profile.state }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="keyword-filter flex-grow md:max-w-xs"
            v-if="
              selectedKeyword &&
              formattedCompetitors &&
              Object.keys(formattedCompetitors).length > 0
            "
          >
            <div class="font-semibold text-sm mb-1">Keyword</div>
            <v-menu
              v-model="menu"
              offset-y
              transition="scale-transition"
              :close-on-content-click="false"
              class="keyword-menu w-full"
            >
              <template v-slot:activator="{ on }">
                <button
                  v-on="on"
                  class="select-keyword py-2 px-4 rounded-lg flex items-center justify-between w-full"
                  :class="{
                    'cursor-not-allowed': isLoadingCompetitors,
                    'text-black': !selectedKeyword,
                    'text-blue-500 font-medium': selectedKeyword,
                  }"
                  :disabled="isLoadingCompetitors || !keywords?.length"
                  style="border: 1px solid #d1d5db"
                >
                  <span class="truncate">{{ selectedKeyword || "Select Keyword" }}</span>
                  <div class="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-4 w-4 text-black ml-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <v-progress-circular
                      v-if="isLoadingCompetitors"
                      indeterminate
                      color="primary"
                      size="20"
                      width="2"
                      class="ml-2"
                    ></v-progress-circular>
                  </div>
                </button>
              </template>

              <v-card class="min-w-[300px] w-full">
                <v-card-title class="text-h6 py-3 px-4 bg-gray-50">
                  Select Keyword
                </v-card-title>
                <v-card-text class="pt-3">
                  <table class="w-full text-left">
                    <thead class="border-b border-gray-200">
                      <tr class="font-bold">
                        <th class="text-sm px-2 pb-3">Keyword</th>
                        <th class="text-sm px-2 pb-3" v-if="hasSearchVolume">
                          Monthly Search Volume
                        </th>
                        <th class="text-sm text-center px-2 pb-3">Rank</th>
                      </tr>
                    </thead>
                    <tbody class="overflow-y-auto max-h-[300px]">
                      <tr
                        v-for="(item, index) in keywordsWithRanks"
                        :key="index"
                        @click="handleKeywordChange(item.keyword)"
                        class="cursor-pointer hover:bg-gray-50 transition-colors duration-150"
                        :class="{'bg-blue-50': item.keyword.toLowerCase() == selectedKeyword?.toLowerCase()}"
                      >
                        <td
                          class="px-2 py-3 max-w-[200px] break-words whitespace-normal"
                          :class="{
                            'text-blue-500 font-medium':
                              item.keyword.toLowerCase() ==
                              selectedKeyword?.toLowerCase(),
                          }"
                        >
                          {{ item.keyword }}
                        </td>
                        <td class="text-center py-3" v-if="hasSearchVolume">
                          {{ item.searchVolume }}
                        </td>
                        <td
                          class="text-center py-3"
                          :class="getRankClass(item.averageRank)"
                        >
                          {{
                            item.averageRank > 20 ? "20+" : item.averageRank
                          }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </v-card-text>
              </v-card>
            </v-menu>
          </div>

          <div class="competitor-search flex-grow md:max-w-xs">
            <div class="font-semibold text-sm mb-1">Add Competitor</div>
            <input
              ref="competitorAutocomplete"
              type="text"
              placeholder="Search for competitors..."
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div
            class="rank-summary flex-grow-0"
            v-if="
              selectedKeyword &&
              formattedCompetitors &&
              Object.keys(formattedCompetitors).length > 0
            "
          >
            <div class="flex gap-6">
              <!-- <div>
                <div class="text-sm font-semibold text-gray-600">Overall Rank</div>
                <div
                  class="font-bold text-xl"
                  :class="{
                    'text-green-600': rankSummary[selectedKeyword]?.avg_rank.toFixed(1) <= 5,
                    'text-orange-500':
                      rankSummary[selectedKeyword]?.avg_rank.toFixed(1) > 5 &&
                      rankSummary[selectedKeyword]?.avg_rank.toFixed(1) <= 10,
                    'text-red-500': rankSummary[selectedKeyword]?.avg_rank.toFixed(1) > 10,
                  }"
                >
                  {{ rankSummary[selectedKeyword]?.avg_rank.toFixed(1) }}
                </div>
              </div> -->
              <div>
                <div class="text-sm font-semibold text-gray-600">
                  <span class="text-blue-500">"{{ selectedKeyword }}"</span> Rank
                </div>
                <div
                  class="font-bold text-xl"
                  :class="{
                    'text-green-600': rankSummary[selectedKeyword]?.avg_rank <= 5,
                    'text-orange-500': rankSummary[selectedKeyword]?.avg_rank > 5 && rankSummary[selectedKeyword]?.avg_rank <= 10,
                    'text-red-500': rankSummary[selectedKeyword]?.avg_rank > 10
                  }"
                >
                {{ rankSummary[selectedKeyword]?.avg_rank }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="selectedGridSourceUrl"
            class="maps-link ml-auto flex items-center gap-2"
          >
            <button
              @click="copyMapsUrl"
              class="flex items-center gap-2 text-blue-500 hover:text-blue-600 py-2 px-3 rounded-md hover:bg-blue-50 transition-colors duration-150"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
              <span>Google Maps Link</span>
            </button>
          </div>
        </div>

        <!-- Main Content Area with Map and Competitors -->
        <div class="flex h-full pt-20">
          <!-- Scrollable Competitors Panel -->
          <div 
            class="w-1/3 h-full overflow-y-auto p-4 pt-0"
            style="margin-top: 24px;"
            v-if="
              selectedKeyword &&
              formattedCompetitors &&
              Object.keys(formattedCompetitors).length > 0
            "
          >
            <div class="pt-4"></div>
            <div
              v-for="(compData, compName, index) in filteredCompetitors"
              :key="compName"
              class="mb-4 p-4 bg-white rounded-lg shadow flex flex-col gap-2 transition-all duration-200"
              :class="{
                'border-l-4 border-blue-500': compData.isMyBusiness,
              }"
              :style="{
                backgroundColor: compData.isMyBusiness
                  ? compData.avgRank <= 5 || compData.rank <= 5
                    ? '#ECFDF5'
                    : compData.avgRank <= 10 || compData.rank <= 10
                    ? '#FEF9C3'
                    : '#FEF2F2'
                  : 'white',
              }"
            >
              <div class="flex items-start justify-between">
                <div class="flex items-start flex-grow">
                  <div class="flex-shrink-0 mr-2 font-semibold text-gray-500" 
                  :class="{
                    'text-green-600': selectedGrid === 'All Grids' && index + 1 <= 5, 
                    'text-orange-500': selectedGrid === 'All Grids' && index+1 > 5 && index + 1 <= 10, 
                    'text-red-500': selectedGrid === 'All Grids' && index + 1 > 10
                    }"
                  >
                    {{ selectedGrid === "All Grids" ? index + 1 : compName + 1 }}.
                  </div>
                  <div>
                    <h3
                      class="text-base font-semibold"
                      :class="{
                        'text-blue-600': compData.isMyBusiness,
                      }"
                    >
                      {{ compData.title }}
                      <span
                        v-if="compData.isMyBusiness"
                        class="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full"
                      >
                        You
                      </span>
                    </h3>
                    <p class="text-sm text-gray-600" v-if="compData.address && typeof compData.address === 'string' && /^[a-zA-Z0-9\s,.\-#()]+$/.test(compData.address.trim()) && compData.address.trim().length > 3">
                      {{ compData.address.trim() }}
                    </p>
                  </div>
                </div>
                <!-- TODO: Add it back when we use multiple grids or keywords -->
                <!-- <div class="flex-shrink-0 text-right">
                  <div
                    class="font-bold"
                    :class="{
                      'text-green-600': getRank(compData) <= 5,
                      'text-orange-500': (getRank(compData) > 5 && getRank(compData) <= 10),
                      'text-red-500': getRank(compData) > 10
                    }"
                  >
                    {{
                      getRank(compData)
                    }}
                  </div>
                </div> -->
              </div>
              <div class="flex items-center gap-1 text-sm text-gray-600">
                <v-icon small color="amber">mdi-star</v-icon>
                {{ compData.average_rating }}
                <div class="h-[5px] w-[5px] bg-gray-400 rounded-full mx-1"></div>
                {{ compData.total_reviews }} Reviews
              </div>
              
              <div class="border-t border-gray-200 pt-2 mt-1">
                <button
                  class="text-blue-500 hover:text-blue-700 text-sm font-medium transition-colors duration-150"
                  @click="openCompetitorDialog(compData, compName)"
                >
                  View Geopoint Ranking
                </button>
              </div>
            </div>
          </div>

          <!-- Map -->
          <div
            class="w-2/3 h-full"
            style="margin-top: 12px;"
            v-if="
              selectedKeyword &&
              formattedCompetitors &&
              Object.keys(formattedCompetitors).length > 0
            "
          >
            <gmap-map
              v-if="selectedKeyword"
              :center="center"
              :zoom="15"
              map-type-id="terrain"
              style="width: 100%; height: 100%"
              class="map-container"
            >
              <gmap-marker
                v-for="(marker, index) in markers"
                :key="index"
                :position="marker.position"
                :icon="generateCustomIcon(marker.rank, marker.gridId)"
                :class="[
                  'marker-circle',
                  { selected: selectedGrid === marker.gridId },
                ]"
                @click="showGridCompetitors(marker.gridId)"
              />

              <gmap-marker
                v-if="markers.length > 4"
                :position="markers[4].position"
                :icon="competitorIcon"
                @click="showGridCompetitors(markers[4].gridId)"
              />
            </gmap-map>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="
            !isLoadingCompetitors &&
            (!formattedCompetitors ||
              Object.keys(formattedCompetitors).length == 0)
          "
          class="absolute top-1/2 left-0 w-full flex justify-center transform -translate-y-1/2"
        >
          <EmptyState message="No data available for the selected location." />
        </div>
      </div>

      <!-- Competitor Dialog -->
      <v-dialog v-model="showCompetitorDialog" max-width="800px">
        <v-card>
          <v-card-title class="bg-gray-50 py-4">
            <span class="text-blue-500 font-medium">
              {{ selectedCompetitor?.title }}
            </span>
            &nbsp;vs&nbsp;
            <span class="text-blue-500 font-medium">
              {{ myBusinessData.title || myBusiness }}
            </span>
          </v-card-title>
          <v-card-subtitle class="text-lg pt-3 font-semibold">
            Keyword:
            <span class="text-blue-500 font-normal">
              {{ selectedKeyword }}
            </span>
          </v-card-subtitle>
          <v-card-text>
            <gmap-map
              :center="mapCenter"
              :zoom="13"
              style="width: 100%; height: 400px"
            >
              <gmap-marker
                v-for="(marker, idx) in competitorMarkers"
                :key="`comp-${idx}`"
                :position="marker.position"
                :icon="generateCustomIcon(marker.rank, marker.gridId, 'dialog')"
              />
            </gmap-map>

            <div class="comparison-section mt-4">
              <h3 class="font-semibold mb-2">Comparison</h3>
              <table class="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
                <thead>
                  <tr class="bg-gray-50 text-left">
                    <th class="py-3 px-4 border-b">Business Name</th>
                    <th class="py-3 px-4 border-b">Avg. Rank</th>
                    <th class="py-3 px-4 border-b">Rating</th>
                    <th class="py-3 px-4 border-b">Reviews</th>
                    <!-- <th class="py-3 px-4 border-b">Categories</th> -->
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="selectedCompetitorData">
                    <td class="py-3 px-4 border-b">
                      {{ selectedCompetitorData.title || myBusiness }}
                    </td>
                    <td class="py-3 px-4 border-b">
                      {{ selectedCompetitorData.avg_rank }}
                    </td>
                    <td class="py-3 px-4 border-b">
                      {{ selectedCompetitorData.average_rating }}
                    </td>
                    <td class="py-3 px-4 border-b">
                      {{ selectedCompetitorData.total_reviews }}
                    </td>
                    <!-- <td class="py-3 px-4 border-b">
                      {{ selectedCompetitorData.category }}
                    </td> -->
                  </tr>
                  <tr class="bg-blue-50">
                    <td class="py-3 px-4 border-b text-blue-600 font-medium">
                      {{ myBusinessData.title || myBusiness }}
                    </td>
                    <td class="py-3 px-4 border-b text-blue-600">
                      {{ rankSummary[selectedKeyword]?.avg_rank.toFixed(1) }}
                    </td>
                    <td class="py-3 px-4 border-b text-blue-600">
                      {{ myBusinessData.average_rating }}
                    </td>
                    <td class="py-3 px-4 border-b text-blue-600">
                      {{ myBusinessData.total_reviews }}
                    </td>
                    <!-- <td class="py-3 px-4 border-b text-blue-600">
                      {{ myBusinessData.category }}
                    </td> -->
                  </tr>
                </tbody>
              </table>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="primary" @click="showCompetitorDialog = false"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </section>
  </div>
</template>

<script>
import {
  collection,
  onSnapshot,
  query,
  where,
  getDoc,
  doc,
  getDocs,
  orderBy,
  limit,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions, colUsers, auth, db } from "@/utils/firebase.utils";
import ToolTip from "@/components/ui/ToolTip.vue";
import Chart from "chart.js/auto";
// import { GmapMap, GmapMarker, GmapInfoWindow } from "vue2-google-maps";
import _ from "lodash";
import CircleMarker from "@/components/ui/common/circleMarker.vue"; // Adjust the path as necessary
import moment from "moment-timezone";
import LocationDropdown from "@/components/LocationDropdown.vue";
import EmptyState from "@/components/ui/common/EmptyState.vue";

export default {
  components: { ToolTip, CircleMarker, LocationDropdown, EmptyState },
  props: {
    stores: {
      type: Array,
      default: () => [], // Default to empty array if no stores are passed
    },
    initialLocationId: {
      type: String,
      default: null,
    },
    initialKeyword: {
      type: String,
      default: null,
    },
    selectedDate: {
      type: String,
      default: null,
    }
  },
  data() {
    return {
      selectedGrid: null,
      gridOptions: Array.from({ length: 9 }, (_, i) => i + 1),
      filteredCompetitors: {},
      competitorIcon: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
      keywords: [],
      formattedCompetitors: {},
      uniqueCompetitors: {},
      selectedKeyword: "",
      tabularData: null,
      dialogVisible: false,
      timelineDialog: false,
      showCompetitorDialog: false,
      dialogCompetitors: [],
      competitorRanks: null,
      myBusinessMarkers: [],
      competitorMarkers: [],
      timelineData: null,
      timelineDates: [],
      menuVisible: false,
      menuPosition: { x: 0, y: 0 },
      myTimelineBusinessData: {
        avgRank: null,
        rank: null,
        gridRanks: {},
      },
      overallAvgRanks: null,
      selectedCompetitorName: null,
      selectedCompetitor: null,
      mapCenter: null,
      myBusiness: "",
      myBusinessData: {},
      averageRankData: null,
      generatedDate: null,
      shape: {
        coords: [10, 10, 10, 15, 15, 15, 15, 10],
        type: "poly",
      },
      keywordsWithRanks: null,
      keywordsWithVolume: null,
      overallRanks: null,
      selectedStore: null,
      menu: false,
      gridMenu: false,
      search: "",

      dateOptions: [],
      isLoadingDates: false,

      tooltipVisible: false,
      tooltipData: "",
      tooltipDate: null,
      tooltipPosition: 0,
      center: { lat: 0, lng: 0 },
      markers: [],
      showStoreDropdown: false,
      selectedWID: null,
      isLoading: false,
      isLoadingCompetitors: false,
      locationSearch: "",
      myPlaceId: null,
      hasSearchVolume: false,
      selectedGridSourceUrl: null,
      rankSummary: null,
      autocomplete: null,
      selectedCompetitorFromSearch: null,
    };
  },
  computed: {
    selectedCompetitorData() {
      // Find the competitor object based on the selectedCompetitorName
      let avgRank = 0;
      let rankSum = 0;
      this.competitorMarkers.forEach((comp) => {
        rankSum += comp.rank;
      });
      avgRank = (rankSum / this.competitorMarkers.length).toFixed(1);
      const data =
        this.competitorMarkers.find(
          (comp) => comp.place_id === this.selectedCompetitorName
        ) || null;
      if (
        data &&
        (!data.avg_rank || data.avg_rank == null || data.avg_rank == undefined)
      ) {
        data.avg_rank = avgRank;
      }
      return data;
    },
    competitorRanksForKeyword() {
      if (!this.selectedKeyword || !this.selectedWID) return {};
      return this.competitorRanks[this.selectedKeyword] || {};
    },
    filteredStores() {
      if (!this.locationSearch) {
        return this.stores;
      }

      const searchTerm = this.locationSearch.toLowerCase();
      return this.stores.filter(
        (store) =>
          store.name?.toLowerCase().includes(searchTerm) ||
          store.sub_locality?.toLowerCase().includes(searchTerm) ||
          store.city?.toLowerCase().includes(searchTerm) ||
          store.state?.toLowerCase().includes(searchTerm)
      );
    },
  },
  watch: {
    selectedWID: {
      handler(newVal) {
        if (newVal) {
          this.$emit('update:selectedWID', newVal);
        }
      },
      immediate: true,
    },
    selectedKeyword: {
      handler() {
        if (
          this.formattedCompetitors &&
          Object.keys(this.formattedCompetitors).length > 0
        ) {
          this.updateTableData();
        }
      },
      immediate: true,
    },
    selectedStore: {
      handler(newStore, oldStore) {
        if (newStore && newStore !== oldStore) {
          // Re-initialize autocomplete when store changes to update location biasing
          this.$nextTick(() => {
            this.initAutocomplete();
          });
        }
      },
    },
  },
  async created() {
    this.generateDateOptions();
    this.parseUrlParams();
  },
  methods: {
    getRank(compData) {
      if (compData.isMyBusiness && this.selectedGrid === "All Grids") {
        return this.rankSummary[this.selectedKeyword]?.avg_rank.toFixed(1);
      } else if(this.selectedGrid !== "All Grids") {
        return compData.rank;
      }
      return compData.avgRank;
    },
    // Add method to parse URL parameters
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const locationId = urlParams.get('location') || this.initialLocationId;
      const keyword = urlParams.get('keyword') || this.initialKeyword;
      
      if (locationId) {
        this.preSelectLocation(locationId);
      }
      
      // We'll handle the keyword selection after location data is loaded
      this.urlKeyword = keyword;
    },
    // With this improved version that prevents redundant navigation:
    modifyUrlParams(param, value) {
      // Check if the current value in the URL is already the same
      if (this.$route.query[param] === value) {
        return; // Skip redundant navigation
      }
      // Only update the URL if the value is different
      this.$router.replace({ query: { ...this.$route.query, [param]: value } });
    },
    // Method to pre-select location based on ID
    async preSelectLocation(locationId) {
      if (this.stores && this.stores.length > 0) {
        const matchingStore = this.stores.find(store => 
          store.wid === locationId || 
          store.placeId === locationId || 
          store.place_id === locationId
        );
        
        if (matchingStore) {
          await this.selectStore(matchingStore); // This now fully awaits population of keywordsWithRanks
          
          // Now that selectStore and its chain of async calls are complete,
          // keywordsWithRanks should be populated.
          if (this.urlKeyword && this.keywordsWithRanks && this.keywordsWithRanks.length > 0) {
            const matchingRankedKeyword = this.keywordsWithRanks.find(kw => 
              kw.keyword.toLowerCase() === this.urlKeyword.toLowerCase()
            );
            
            if (matchingRankedKeyword) {
              this.handleKeywordChange(matchingRankedKeyword.keyword);
            } else {
               console.warn(`URL keyword '${this.urlKeyword}' not found in ranked keywords for ${locationId}. Selecting first available ranked keyword.`);
              this.handleKeywordChange(this.keywordsWithRanks[0].keyword);
            }
          } else if (this.keywordsWithRanks && this.keywordsWithRanks.length > 0) {
            // If no urlKeyword, but keywordsWithRanks is available, select the first.
            this.handleKeywordChange(this.keywordsWithRanks[0].keyword);
          } else {
            console.warn("No keywords available to select after store selection for locationId: ", locationId, " and urlKeyword: ", this.urlKeyword);
          }
        }
      } else {
        // If stores are not yet loaded, wait for them and then try pre-selecting.
        // This might happen if this component loads and parses URL params before `stores` prop is populated.
        this.$watch('stores', (newStores) => {
          if (newStores && newStores.length > 0) {
            this.preSelectLocation(locationId); // Retry once stores are available
          }
        }, { once: true }); // Watch only once
      }
    },
    showMenu(date, event) {
      this.computeMyBusinessRank(date);
      this.tooltipDate = date;
      const keywordData = this.timelineData[date]?.[this.selectedKeyword];

      if (keywordData) {
        const firstEntry = keywordData[1][0];
        this.tooltipData = {
          title: firstEntry.title,
          average_rating: firstEntry.average_rating,
          total_reviews: firstEntry.total_reviews,
        };
      } else {
        this.tooltipData = null;
      }
      const rect = event.target.getBoundingClientRect();
      this.menuPosition = {
        x: rect.left + rect.width / 2,
        y: rect.top - 120,
      };
      this.menuVisible = true;
    },
    handleDateClick() {},
    computeMyBusinessRank(date) {
      const keywordData = this.timelineData[date]?.[this.selectedKeyword];
      if (!keywordData) return;
      let totalRank = 0;

      let gridIds = Object.keys(keywordData); // the actual grid IDs
      let totalGrids = gridIds.length; // e.g. 9
      Object.keys(keywordData).forEach((gridId) => {
        const gridData = keywordData[gridId];
        const myBusiness = gridData.find((comp) => comp.is_my_business);
        // For testing
        // const myBusiness = gridData.find(
        //   (comp) => comp.title == "The Bake Shop"
        // );

        if (myBusiness) {
          this.myTimelineBusinessData.title = myBusiness.title;
          this.myTimelineBusinessData.gridRanks[gridId] = myBusiness.rank;
        } else {
          // Assign last rank if missing
          this.myTimelineBusinessData.gridRanks[gridId] = gridData.length + 1;
        }
        totalRank += this.myTimelineBusinessData.gridRanks[gridId];
      });

      if (this.selectedGrid === "All Grids") {
        this.myTimelineBusinessData.avgRank =
          totalRank > 0 ? (totalRank / totalGrids).toFixed(1) : "N/A";
      } else {
        this.myTimelineBusinessData.rank =
          this.myTimelineBusinessData.gridRanks[this.selectedGrid] || "N/A";
      }
      console.log("this.myTimelineBusinessData:", this.myTimelineBusinessData);
    },
    formatDate(date) {
      // Format the date as needed
      return new Date(date).toLocaleDateString();
    },
    generateCustomIcon(rank, gridId, type = null) {
      const color = rank < 5 ? "green" : rank < 10 ? "orange" : "red";
      const borderColor = this.selectedGrid === gridId ? "blue" : "white";
      const showGridText = type !== "dialog";

      const svg = `
        <svg xmlns='http://www.w3.org/2000/svg' width='90' height='90' viewBox='0 0 90 90' >
          <circle cx='20' cy='20' r='18' fill='${color}' stroke='${borderColor}' stroke-width='2'/>
           <text x='20' y='20' font-size='14' font-weight='bold' text-anchor='middle' dominant-baseline='central' fill='white'>${
             rank > 20 ? "20+" : rank
           }</text>
          ${
            showGridText
              ? `
                  <text x='62' y='6' font-size='15' font-weight='bold' text-anchor='middle' dominant-baseline='central' fill='#000000'>Geopoint</text>
                  <text x='40' y='18' font-size='17' font-weight='bold' text-anchor='middle' dominant-baseline='central' fill='#000000'>${gridId}</text>
                `
              : ""
          }
        </svg>
      `;
      return {
        url: "data:image/svg+xml;base64," + btoa(svg),
        scaledSize: { width: 95, height: 95 },
        anchor: { x: 20, y: 20 }, // Center alignment
      };
    },
    async fetchMyBusinessData() {
      try {
        if (!this.currentUID || !this.selectedWID) {
          console.warn('Missing required IDs:', { currentUID: this.currentUID, selectedWID: this.selectedWID });
          return;
        }

        console.log("Fetching business data for:", { currentUID: this.currentUID, selectedWID: this.selectedWID });
        
        // Fetch keywords data
        const keywordsRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.selectedWID,
          "keywords"
        );

        const keywordsSnapshot = await getDocs(
          query(keywordsRef, orderBy("created_at", "desc"), limit(1))
        );

        if (!keywordsSnapshot.empty) {
          const keywordsDoc = keywordsSnapshot.docs[0].data();
          if (keywordsDoc.keywords) {
            // If no volume data but has keywords array
            this.hasSearchVolume = false;
            this.keywordsWithVolume = keywordsDoc.keywords.map((keyword) => ({
              keyword: keyword,
              searchVolume: 0
            }));
          } else if (
            keywordsDoc.keywords_volume_data &&
            keywordsDoc.keywords_volume_data.keywords
          ) {
            const keywordsData = keywordsDoc.keywords_volume_data?.keywordsData;
            this.hasSearchVolume = true;
            this.keywordsWithVolume = keywordsData?.map((keyword) => ({
              keyword: keyword.keyword,
              searchVolume: keyword.search_volume || 0
            })) || [];
          }
        }

        if (this.keywordsWithVolume) {
          this.keywordsWithVolume.sort((a, b) => b.searchVolume - a.searchVolume);
        }

        // Try to get business data from connected-apps first
        const gbpRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.selectedWID,
          "connected-apps",
          "google_business_profile"
        );

        try {
          const gbpDoc = await getDoc(gbpRef);
          if (gbpDoc.exists()) {
            const gbpData = gbpDoc.data();
            if (gbpData?.accounts?.[0]?.locations?.[0]) {
              const location = gbpData.accounts[0].locations[0];
              this.myBusiness = location.title;
              this.myPlaceId = location.original_place_id || location.placeId;
              return; // Exit if we successfully got the data
            }
          }
        } catch (error) {
          console.warn("Error fetching GBP connected app data:", error);
          // Continue to try workspace data
        }

        // Fallback to workspace data if connected-app data not available
        try {
          const workspaceRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.selectedWID
          );
          const workspaceDoc = await getDoc(workspaceRef);
          
          if (workspaceDoc.exists()) {
            const workspaceData = workspaceDoc.data();
            if (workspaceData.shallowConnect !== undefined) {
              this.myBusiness = workspaceData.name || 'Unknown Business';
              this.myPlaceId = workspaceData.place_id;
            }
          }
        } catch (error) {
          console.error("Error fetching workspace data:", error);
          this.$snackbar.show({
            message: "Error loading business data. Please try again.",
            color: "error"
          });
        }
      } catch (error) {
        console.error("Error in fetchMyBusinessData:", error);
        this.$snackbar.show({
          message: "Error loading business data. Please try again.",
          color: "error"
        });
      }
    },
    updateTableData() {
      if (this.selectedGrid === "All Grids") {
        // Show competitors for all grids for the selected keyword
        this.filteredCompetitors = this.competitorRanks[this.selectedKeyword];
      } else if (this.selectedGrid) {
        // Filter competitors based on selected grid
        this.filteredCompetitors =
          this.formattedCompetitors[this.selectedKeyword][this.selectedGrid] ||
          {};
      } else {
        // If no grid is selected, show aggregate data
        this.filteredCompetitors = this.competitorRanks[this.selectedKeyword];
      }
      this.loadMarkersForKeyword(this.selectedKeyword);
    },
    openCompetitorDialog(comp, title) {
      // console.log("comp:", comp);
      this.selectedCompetitor = { ...comp }; // Use a spread operator to avoid direct mutation
      this.selectedCompetitor.title = comp.title;
      this.selectedCompetitorName = comp.place_id;
      this.competitorMarkers = [];
      this.myBusinessMarkers = [];

      // Grab the arrays for each grid for the selected keyword
      const gridData = this.formattedCompetitors[this.selectedKeyword];
      if (!gridData) return;

      // We'll search for the competitor and your business in each grid,
      // and create a marker for each grid they appear in
      let rankSum = 0;

      Object.keys(gridData).forEach((gridId) => {
        const compArray = gridData[gridId]; // array of competitor objects for this grid
        // Find the clicked competitor in this grid        const myBusinessRank =
        const businessRank =
          compArray.find((item) => item.place_id === comp.place_id)?.rank || 21; // Get the rank or null if not found
        const competitorObj = compArray.find(
          (x) => x.place_id === comp.place_id
        );
        const firstComp = compArray[0];

        if (competitorObj) {
          const [lat, lng] = competitorObj.geo_location.split(",").map(Number);
          if (isNaN(lat) || isNaN(lng)) {
            console.error("Invalid lat/lng:", lat, lng);
            return; // Skip this marker if invalid
          }
          rankSum += businessRank;
          this.competitorMarkers.push({
            position: { lat, lng },
            gridId,
            rank: businessRank,
            title: comp.title || title,
            place_id: comp.place_id,
            average_rating: comp.average_rating,
            total_reviews: comp.total_reviews,
            category: comp.category,
            avg_rank: comp.avgRank ? comp.avgRank : null,
          });
        } else {
          const [lat, lng] = firstComp.geo_location.split(",").map(Number);
          if (isNaN(lat) || isNaN(lng)) {
            console.error("Invalid lat/lng:", lat, lng);
            return; // Skip this marker if invalid
          }
          this.competitorMarkers.push({
            position: { lat, lng },
            gridId,
            rank: businessRank,
            title: comp.title || title,
            place_id: comp.place_id,
            average_rating: comp.average_rating,
            total_reviews: comp.total_reviews,
            category: comp.category,
            avg_rank: comp.avgRank ? comp.avgRank : null,
          });
        }

        console.log("this.competitorMarkers:", this.competitorMarkers);
      });

      // Center the map on the competitor's first marker if available,
      // else center on your business's first marker, else fallback
      if (this.competitorMarkers.length) {
        this.mapCenter = this.competitorMarkers[0].position;
      } else if (this.myBusinessMarkers.length) {
        this.mapCenter = this.myBusinessMarkers[0].position;
      } else {
        this.mapCenter = { lat: 18.5728, lng: 73.7859 }; // fallback
      }

      // Finally show the dialog
      this.showCompetitorDialog = true;
    },
    async fetchData() {
      this.formattedCompetitors = {};

      if (!this.selectedWID) {
        this.$snackbar.show({
          message: "Please select a store before proceeding.",
          color: "error",
        });
        return;
      }
      await this.fetchCompetitorData();
      this.loadMarkersForKeyword(this.selectedKeyword);
      // TODO: Uncomment this when we use multiple grids or keywords
      // this.selectedGrid = "All Grids";
      this.selectedGrid = 5;
      this.updateTableData();
    },
    async handleKeywordChange(keyword) {
      this.selectedKeyword = keyword;
      // modify the param according to the modified keyword: add keyword = keyword to the url
      this.modifyUrlParams("keyword", keyword);
      this.menu = false;
      this.myBusinessData.keyword_avg_rank =
        parseFloat(
          this.overallRanks[this.myPlaceId]?.keywordRanks?.[keyword]?.toFixed(1)
        ) || "21";
      if (this.selectedGrid && this.selectedGrid != "All Grids") {
        const keywordData = this.formattedCompetitors[keyword] || {};
        // console.log("keywordData:", keywordData);
        const gridData = keywordData[this.selectedGrid] || [];
        // this.selectedGrid = 5;
        // this.showGridCompetitors(5);
        // const gridData = keywordData["5"] || [];
        this.selectedGridSourceUrl = gridData[0]?.maps_url || null;
      }
      else {
        // TODO: make selectedGridSourceUrl to null when we use multiple grids
        if (this.formattedCompetitors[keyword] && this.formattedCompetitors[keyword]["5"]) {
          const gridData = this.formattedCompetitors[keyword]["5"];
          this.selectedGridSourceUrl = gridData[0]?.maps_url || null;
        } else {
          this.selectedGridSourceUrl = null;
        }
      }
      this.timelineDates = Object.keys(this.timelineData)?.map((date) => {
        // console.log("date:::", date);
        const keywordData = this.timelineData[date]?.[keyword];
        if (!keywordData) return { date, avgRank: "N/A" };
        let totalRank = 0;
        let count = 0;

        Object.keys(keywordData).forEach((gridId) => {
          const gridData = keywordData[gridId];
          const myBusiness = gridData.find((comp) => comp.is_my_business);
          // For testing
          // const myBusiness = gridData.find(
          //   (comp) => comp.title == "The Bake Shop"
          // );
          if (myBusiness) {
            totalRank += myBusiness.rank;
          } else {
            totalRank += gridData.length + 1; // Assign last rank if missing
          }
          count++;
        });

        const avgRank = count > 0 ? (totalRank / count).toFixed(1) : "N/A";
        return { date, avgRank };
      });
    },

    async computeCompetitorRanks(competitorData) {
      let competitorRanks = {}; // per keyword
      // let overallRanks = {};    // aggregated across all keywords

      // 1) For each keyword
      Object.keys(competitorData).forEach((keyword) => {
        let keywordData = competitorData[keyword]; // e.g. { "1": [ ... ], "2": [ ... ], ... }
        let gridIds = Object.keys(keywordData); // the actual grid IDs
        let keywordCompetitors = {}; // collects data per competitor

        // 2) Build a map of (compName => { sum: 0, count: 0, gridRanks: {} })
        //    But we won't finalize the sum until we check missing grids
        //    So let's first gather a quick index by grid:
        let gridIndexById = {};

        Object.keys(keywordData).forEach((gridId) => {
          let compsInGrid = keywordData[gridId];
          // (Optional) If needed, sort compsInGrid by rank ascending
          // compsInGrid.sort((a, b) => a.rank - b.rank);

          // Build a dictionary for quick "find competitor by name"
          // so we know who is missing, and what the grid size is
          const gridMap = {};
          compsInGrid.forEach((comp) => {
            // console.log("comp:", comp);
            gridMap[comp.place_id] = comp;
          });
          // store: gridIndexById[gridId] = { map of compName => competitor }, plus size
          gridIndexById[gridId] = {
            compMap: gridMap,
            gridSize: parseInt(compsInGrid.length),
          };
        });

        // 3) We want to gather a set of all competitor names (across all grids),
        //    so we can handle missing ones too.
        let allCompNames = new Set();
        Object.keys(keywordData).forEach((gridId) => {
          let compsInGrid = keywordData[gridId];
          compsInGrid.forEach((comp) => {
            allCompNames.add(comp.place_id);
          });
        });

        // 4) Initialize data for each competitor
        allCompNames.forEach((placeId) => {
          keywordCompetitors[placeId] = {
            gridRanks: {},
            totalRank: 0, // We'll accumulate real or "missing" rank here
          };
        });

        // 5) For each grid, if competitor is found => add actual rank
        //    else => add (gridSize + 1)
        gridIds.forEach((gridId) => {
          const { compMap, gridSize } = gridIndexById[gridId];

          // For each competitor name in allCompNames
          allCompNames.forEach((placeId) => {
            if (compMap && compMap[placeId]) {
              // console.log("compMap[name]:", compMap[name]);
              // competitor is present
              let actualRank = compMap[placeId].rank; // or index-based rank if you prefer
              keywordCompetitors[placeId].gridRanks[gridId] = actualRank;
              keywordCompetitors[placeId].totalRank += actualRank;
              keywordCompetitors[placeId].average_rating =
                compMap[placeId].average_rating;
              keywordCompetitors[placeId].total_reviews =
                compMap[placeId].total_reviews;
              keywordCompetitors[placeId].category = compMap[placeId].category;
              keywordCompetitors[placeId].title = compMap[placeId].title;
              keywordCompetitors[placeId].place_id = compMap[placeId].place_id;
              keywordCompetitors[placeId].isMyBusiness =
                compMap[placeId].isMyBusiness || false;
              keywordCompetitors[placeId].address =
                compMap[placeId].address || "";
            } else {
              keywordCompetitors[placeId].gridRanks[gridId] = gridSize + 1;
              keywordCompetitors[placeId].totalRank += gridSize + 1;
            }
          });
        });

        // 6) Now each competitor has a totalRank across all grids. The # of grids is gridIds.length.
        Object.keys(keywordCompetitors).forEach((placeId) => {
          let data = keywordCompetitors[placeId];
          let gridLen = Object.keys(data.gridRanks).length;
          let avg = data.totalRank / gridLen;
          data.avgRank = avg.toFixed(1);
        });

        // **Sort competitors by avgRank in decreasing order**
        const sortedCompetitors = Object.entries(keywordCompetitors)
          .sort(([, a], [, b]) => a.avgRank - b.avgRank) // Sort in decreasing order
          .reduce((acc, [name, data]) => {
            acc[name] = data;
            return acc;
          }, {});

        competitorRanks[keyword] = sortedCompetitors;
      });

      // 7) Compute overall average ranks across all keywords (if needed)
      let overallRanks = {};
      // First, identify all available keywords
      const allKeywords = Object.keys(competitorRanks);
      // Then identify all businesses across all keywords
      const allBusinesses = new Set();
      allKeywords.forEach((keyword) => {
        Object.keys(competitorRanks[keyword]).forEach((placeId) => {
          allBusinesses.add(placeId);
        });
      });

      // Initialize all businesses with all keywords
      allBusinesses.forEach((placeId) => {
        if (!overallRanks[placeId]) {
          overallRanks[placeId] = {
            keywordRanks: {},
            totalRank: 0,
            keywordCount: 0,
            isMyBusiness: false,
          };
        }

        // Initialize with placeholder data (will be updated later)
        allKeywords.forEach((keyword) => {
          overallRanks[placeId].keywordRanks[keyword] = null;
        });
      });

      // Fill in actual data
      Object.keys(competitorRanks).forEach((keyword) => {
        let keywordData = competitorRanks[keyword];

        // For each business in this keyword's data, update its info
        Object.keys(keywordData).forEach((placeId) => {
          let avgRank = parseFloat(keywordData[placeId].avgRank);
          overallRanks[placeId].keywordRanks[keyword] = avgRank;
          overallRanks[placeId].totalRank += avgRank;
          overallRanks[placeId].keywordCount += 1;
          overallRanks[placeId].isMyBusiness =
            keywordData[placeId].isMyBusiness;
          overallRanks[placeId].place_id = keywordData[placeId].place_id;
          overallRanks[placeId].title = keywordData[placeId].title;
        });
      });

      // Add rank 21 for all keywords where my business isn't present
      const myBusinessId = this.myPlaceId;
      if (myBusinessId && overallRanks[myBusinessId]) {
        allKeywords.forEach((keyword) => {
          if (!competitorRanks[keyword][myBusinessId]) {
            // Business not found for this keyword, assign rank 21
            overallRanks[myBusinessId].keywordRanks[keyword] = 21;
            overallRanks[myBusinessId].totalRank += 21;
            overallRanks[myBusinessId].keywordCount += 1;
          }
        });
      }

      // Finalize overall average
      Object.keys(overallRanks).forEach((placeId) => {
        let data = overallRanks[placeId];
        data.overallAvgRank = data.totalRank / data.keywordCount;
      });

      // console.log("competitorRanks:", competitorRanks);
      console.log("overallRanks:", overallRanks);
      this.competitorRanks = competitorRanks;
      this.overallRanks = overallRanks;
      return { competitorRanks, overallRanks };
    },

    getRankClass(rank) {
      return rank > 5 ? "text-red-500" : "text-green-500";
    },
    getChangeClass(change) {
      return change > 0 ? "text-green-500" : "text-red-500";
    },
    showTooltip(date) {
      this.tooltipDate = date;
      // console.log("this.timelineData:", this.timelineData);
      const keywordData = this.timelineData[date]?.[this.selectedKeyword];
      // console.log("keywordData:", keywordData);
      if (keywordData) {
        const firstEntry = keywordData[1][0]; // Assuming you want the first entry
        // console.log("firstEntry:", firstEntry);
        this.tooltipData = {
          title: firstEntry.title,
          average_rating: firstEntry.average_rating,
          total_reviews: firstEntry.total_reviews,
        };
      } else {
        this.tooltipData = null;
      }
      // console.log("this.tooltipData:", this.tooltipData);
      this.tooltipVisible = true;
      const tooltip = event.target.querySelector(".tooltip");
      // if (tooltip) {
      //   tooltip.style.left = '50%';
      //   tooltip.style.bottom = '100%';
      // }
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
    generateDateOptions() {
      this.dateOptions = [
        {
          text: "Last 1 Month",
          value: {
            months: 1,
          },
        },
        {
          text: "Last 3 Months",
          value: {
            months: 3,
          },
        },
        {
          text: "Last 6 Months",
          value: {
            months: 6,
          },
        },
        {
          text: "Last 1 Year",
          value: {
            months: 12,
          },
        },
      ];
    },
    async selectDateRange() {},

    handleClickOutside(event) {
      const profileDropdown = this.$refs.showStoreDropdown;
      if (profileDropdown && !profileDropdown.contains(event.target)) {
        this.closeProfileDropdown();
      }
    },
    closeProfileDropdown() {
      if (this.showStoreDropdown) {
        this.showStoreDropdown = false;
      }
    },
    toggleStoreDropdown(event) {
      this.showStoreDropdown = !this.showStoreDropdown;
      event.stopPropagation();
    },
    async selectStore(profile) {
      this.hasSearchVolume = false;
      this.selectedStore = profile;
      this.selectedGridSourceUrl = null;
      this.keywords = [];
      this.formattedCompetitors = {};
      this.selectedWID = profile.wid;
      await this.fetchStorePosts(profile); // Contains fetchMyBusinessData and fetchKeywords
      // await this.fetchKeywords(); // fetchKeywords is already called in fetchStorePosts, this might be redundant or for a different purpose
      this.showStoreDropdown = false;
      await this.fetchData(); // Ensure fetchData completes before selectStore resolves
      
      // Re-initialize autocomplete with new store location for biasing
      this.$nextTick(() => {
        this.initAutocomplete();
      });
    },
    async fetchStorePosts() {
      if (!this.selectedWID) {
        this.$snackbar.show({
          message: "Please select a store before proceeding.",
          color: "error",
        });
        return;
      } else {
        this.formattedCompetitors = {};
        this.competitorRanks = {};
        this.overallRanks = {};
        this.myBusinessMarkers = [];
        this.filteredCompetitors = null;
        await this.fetchMyBusinessData();
      }
    },
    async fetchKeywords() {
      try {
        if (!this.currentUID || !this.selectedWID) {
          console.log("Missing required IDs");
          return [];
        }

        const keywordsRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.selectedWID,
          "keywords"
        );

        // Get the most recent keywords document
        const keywordsSnapshot = await getDocs(
          query(keywordsRef, orderBy("created_at", "desc"), limit(1))
        );

        let fetchedKeywords = [];
        if (!keywordsSnapshot.empty) {
          const keywordsDoc = keywordsSnapshot.docs[0].data();
          // Extract keywords array from the document
          fetchedKeywords = keywordsDoc.keywords || [];

          // If there's modified_business_api_keywords, use that instead
          if (keywordsDoc.keywords && keywordsDoc.keywords.length > 0) {
            fetchedKeywords = keywordsDoc.keywords;
          }
          // Only fallback to modified_business_api_keywords if keywords doesn't exist or is empty
          else if (
            keywordsDoc.modified_business_api_keywords &&
            keywordsDoc.modified_business_api_keywords.length > 0
          ) {
            fetchedKeywords = keywordsDoc.modified_business_api_keywords;
          }

          // console.log("Fetched keywords:", fetchedKeywords);
          this.keywords = fetchedKeywords;
          return fetchedKeywords;
        } else {
          console.log("No keywords found");
          this.keywords = [];
          return [];
        }
      } catch (error) {
        console.error("Error fetching keywords:", error);
        this.$snackbar.show({
          message: "Error fetching keywords",
          color: "error",
        });
        this.keywords = [];
        return [];
      }
    },
    loadMarkersForKeyword(keyword) {
      // If that keyword doesn't exist yet, bail out
      if (!this.formattedCompetitors[keyword]) {
        this.markers = [];
        return;
      }
      const gridData = this.formattedCompetitors[keyword];
      // We only want one marker per grid => 9 total, so we pick the first competitor
      const newMarkers = [];

      // For each of the available grids:
      Object.keys(gridData).forEach((gridId) => {
        const competitorList = gridData[gridId];
        if (!competitorList || competitorList.length === 0) return;

        // Use the FIRST competitor for lat/lng (all have same anyway)
        const firstComp = competitorList[0];
        const [latitude, longitude] = firstComp?.geo_location
          ?.split(",")
          .map(Number);
        const lat = latitude || 0;
        const lng = longitude || 0;
        const myBusinessRank =
          competitorList.find((comp) => {
            // Compare place_id directly
            return comp.place_id === this.myPlaceId;
          })?.rank || 21;
        newMarkers.push({
          gridId: gridId, // Use the actual gridId from the loop
          position: { lat, lng },
          rank: myBusinessRank,
        });
      });

      this.markers = newMarkers;

      // console.log("this.markers:", this.markers);
      // Center on grid #5 if found
      const middleMarker = this.markers.find((m) => m.gridId === "5");
      if (middleMarker) {
        this.center = this.shiftCenter(middleMarker.position);
      } else if (this.markers.length) {
        this.center = this.shiftCenter(this.markers[0].position);
      }
      const sortedMarkers = [...this.markers].sort(
        (a, b) => parseInt(a.gridId) - parseInt(b.gridId)
      );
      const middleIndex = Math.floor(sortedMarkers.length / 2);
      this.center = this.shiftCenter(sortedMarkers[middleIndex].position);
    },
    shiftCenter(position) {
      // Adjust these values to shift the center
      const latShift = -0.001; // Shift latitude
      const lngShift = -0.001; // Shift longitude

      return {
        lat: position.lat + latShift,
        lng: position.lng + lngShift,
      };
    },
    sortCompetitorsAsscending(competitors) {
      return competitors.sort((a, b) => {
        return (a.rank || 0) - (b.rank || 0); // Sort in descending order
      });
    },
    copyMapsUrl() {
      if (this.selectedGridSourceUrl) {
        navigator.clipboard
          .writeText(this.selectedGridSourceUrl)
          .then(() => {
            // Optional: Show a success message
            this.$snackbar.show({
              message: "Link copied to clipboard. Please open this link in Incognito mode only. Results you see on Maps may vary if its more than 24 hours since report generation.",
              color: "success",
            });
          })
          .catch((err) => {
            console.error("Failed to copy text: ", err);
            this.$snackbar.show({
              message: "Failed to copy link",
              color: "error",
            });
          });
      }
    },
    // Called when user clicks a marker => show its grid's competitors in dialog
    showGridCompetitors(gridId) {
      // console.log("showGridCompetitors:", gridId);
      if (this.selectedGrid == gridId) {
        this.selectedGrid = "All Grids";
        this.selectedGridSourceUrl = null;
        this.updateTableData();
        return;
      }
      this.selectedGrid = gridId;
      // <-- ADDED or MODIFIED
      if (!this.selectedKeyword) return;
      const keywordData = this.formattedCompetitors[this.selectedKeyword] || {};
      const competitorList = keywordData[gridId] || [];
      // console.log("competitorList:", competitorList);
      if (competitorList) {
        const myBusiness = competitorList.find((comp) => comp.isMyBusiness);
        this.myBusinessData.rank = myBusiness ? myBusiness.rank : "20+";
        this.selectedGridSourceUrl = competitorList[0]?.maps_url || null;
      }
      this.dialogCompetitors = this.sortCompetitorsAsscending(competitorList);
      this.updateTableData();

      // this.dialogVisible = true;
    },
    async fetchCompetitorData() {
      try {
        if (!this.selectedWID) {
          return;
        }
        this.isLoadingCompetitors = true;
        this.$loader.show();
        const uid = this.currentUID;

        const payload = {
          uid,
          wid: this.selectedWID,
          source: "competitors",
          keywords: this.keywords, // Use existing keywords from state
          date: this.selectedDate,
        };
        let result ={data: {}};
        const idToken = await auth.currentUser.getIdToken();
        // check how much time it tokk to get the response
        const startTime = Date.now();
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/fetchCompetitorAnalysisReport`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ data: payload }),
          }
        );
        if (response.ok) {
          const endTime = Date.now();
          const duration = endTime - startTime;
          console.log(`Time taken: ${duration} milliseconds`);
          let res = await response.json(); // <-- THIS IS THE KEY STEP
          // console.log("Success! Data from server:", res);
          result.data = {...res}
        }
        const competitorsByKeyword = {};
        console.log("result:", result.data);
        this.rankSummary = result.data.summary;
        this.uniqueCompetitors = new Set(
          result.data.data.map((item) => item.place_id)
        );

        let reportDate = null; // Initialize reportDate

        if (result.data && result.data.data && result.data.data.length > 0) {
          reportDate = result.data.data[0].date; // Extract date from the first data item

          this.timelineData = result.data.dateWiseData;
          // this.timelineDates = Object.keys(result.data.dateWiseData);

          result.data.data?.forEach((item) => {
            const {
              keyword,
              grid_id,
              average_rating,
              categories,
              geo_location,
              location_id,
              place_id,
              rank,
              title,
              total_reviews,
              uid,
              wid,
              date,
              business_name,
              is_my_business,
              address,
              maps_url,
              location_variable_keyword,
            } = item;

            if (!competitorsByKeyword[keyword]) {
              competitorsByKeyword[keyword] = {};
            }
            if (!competitorsByKeyword[keyword][grid_id]) {
              competitorsByKeyword[keyword][grid_id] = [];
            }

            let isMyBusiness = place_id === this.myPlaceId;
            competitorsByKeyword[keyword][grid_id].push({
              business_name,
              title,
              average_rating,
              category: categories,
              grid_id,
              geo_location,
              location_id,
              uid,
              wid,
              rank,
              total_reviews,
              place_id,
              date,
              isMyBusiness: is_my_business || isMyBusiness,
              address,
              maps_url,
              location_variable_keyword
            });
            competitorsByKeyword[keyword][grid_id].sort(
              (a, b) => a.rank - b.rank
            );

            if (isMyBusiness || is_my_business) {
              this.myBusinessData.average_rating = average_rating || "N/A";
              this.myBusinessData.total_reviews = total_reviews || "N/A";
              this.myBusinessData.category = categories || "N/A";
              this.myBusinessData.title = title || "N/A";
              // console.log("this.myBusinessData:", this.myBusinessData);
            }
          });
        }

        if (reportDate) {
          this.$emit("rank-data-date-updated", reportDate); // Emit the extracted date
        }
        // console.log("Formatted markers:", this.markers);
        this.formattedCompetitors = competitorsByKeyword;
        // TODO: Remove this when we use multiple grids
        this.selectedGrid = 5;
        await this.computeCompetitorRanks(competitorsByKeyword);
        // await this.fetchKeywords();
        // Extract unique keywords for dropdown/filtering
        this.keywords = Object.keys(competitorsByKeyword);
        // console.log('this.myBusiness,:', this.myBusiness, this.overallRanks[this.myBusiness])
        this.keywordsWithRanks = this.keywords.map((keyword) => {
          this.myBusinessData.avg_rank = parseFloat(
            this.rankSummary[keyword]?.avg_rank?.toFixed(1) ||
              "21"
          );
          const avgRank = parseFloat(
            this.overallRanks[this.myPlaceId]?.keywordRanks?.[keyword]?.toFixed(
              1
            ) || "21"
          );

          // Find the search volume for the current keyword
          const keywordData = this.keywordsWithVolume?.find(
            (data) => data.keyword === keyword
          );
          const searchVolume = keywordData ? keywordData.searchVolume : 0;
          // const avgRank = this.overallRanks[this.myBusiness]?.overallAvgRank || "N/A"; // Use "N/A" if no rank found
          return {
            keyword,
            averageRank: parseFloat(avgRank),
            searchVolume,
          };
        });
        // console.log("this.keywordsWithRanks:", this.keywordsWithRanks);
        this.selectedKeyword = this.keywordsWithRanks[0].keyword;
        if (this.$route.query.keyword) {
          const matchingKeyword = this.keywordsWithRanks.find(
            k => k.keyword.toLowerCase() === this.$route.query.keyword.toLowerCase()
          );
          
          if (matchingKeyword) {
            this.selectedKeyword = matchingKeyword.keyword;
          } else {
            this.selectedKeyword = this.keywordsWithRanks[0].keyword;
          }
        }
        this.modifyUrlParams("keyword", this.selectedKeyword);
        this.myBusinessData.keyword_avg_rank = parseFloat(
          this.overallRanks[
            Object.keys(this.overallRanks).find(
              (placeId) =>
                this.overallRanks[placeId].place_id === this.myPlaceId
            )
          ]?.keywordRanks?.[this.selectedKeyword]?.toFixed(1) || "21"
        );
        // this.showGridCompetitors(5);
        console.log("Processed Competitor Data:", this.formattedCompetitors);
        let firstGridData =
          this.formattedCompetitors[this.selectedKeyword]["5"];
          // TODO: Remove this when we use multiple grids or keywords
          this.$set(this, 'selectedGridSourceUrl', firstGridData[0]?.maps_url || null);

        this.timelineDates = Object.keys(result.data.dateWiseData).map(
          (date) => {
            const keywordData = this.timelineData[date]?.[this.selectedKeyword];
            if (!keywordData) return { date, avgRank: "N/A" };

            let totalRank = 0;
            let count = 0;

            Object.keys(keywordData).forEach((gridId) => {
              const gridData = keywordData[gridId];
              const myBusiness = gridData.find((comp) => comp.is_my_business || comp.isMyBusiness);

              if (myBusiness) {
                totalRank += myBusiness.rank;
              } else {
                totalRank += gridData.length + 1; // Assign last rank if missing
              }
              count++;
            });

            const avgRank = count > 0 ? (totalRank / count).toFixed(1) : "N/A";
            return { date, avgRank };
          }
        );
        // console.log("this.timelineData:", this.timelineData);
        // console.log("this.timelineDates:", this.timelineDates);
      } catch (error) {
        console.error("Error fetching competitor data:", error);
        // this.competitors = []; // Set empty array on error
      } finally {
        this.isLoadingCompetitors = false;
        this.$loader.hide();
      }
    },
    initAutocomplete() {
      if (!this.$refs.competitorAutocomplete) {
        console.warn("Competitor autocomplete input not found");
        return;
      }
      
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.warn("Google Maps Places API not loaded");
        // Try again after a delay
        setTimeout(() => {
          this.initAutocomplete();
        }, 1000);
        return;
      }
      
      if (this.autocomplete) {
        this.autocomplete = null;
      }
      
      const input = this.$refs.competitorAutocomplete;

      if (!(input instanceof HTMLElement)) {
        console.error("Competitor autocomplete input is not a valid HTML Element.");
        return;
      }

      try {
        this.autocomplete = new window.google.maps.places.PlaceAutocompleteElement({
          inputElement: input,
          types: ['establishment'],
        });

        // Location biasing if store is selected
        if (this.selectedStore && (this.selectedStore.gmbProfile?.location || this.selectedStore.location)) {
          const storeLocation = this.selectedStore.gmbProfile?.location || this.selectedStore.location;
          if (storeLocation.lat && storeLocation.lng) {
            // For location biasing in the new API, we might need to use locationBias or locationRestriction
            // This is a placeholder for now as the exact implementation depends on the API version
            console.log("Store location for biasing:", storeLocation);
          }
        }

        this.autocomplete.addEventListener('gmp-placeselect', async (event) => {
          const place = event.place;
          if (!place || !place.id) return;

          try {
            await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location', 'id'] });

            this.selectedCompetitorFromSearch = {
              placeId: place.id,
              name: place.displayName,
              address: place.formattedAddress,
              location: place.location
            };

            this.$snackbar.show({
              text: `Selected competitor: ${place.displayName}`,
              color: "success"
            });

            // Clear the input
            input.value = '';
          } catch (error) {
            console.error("Error fetching place details:", error);
            this.$snackbar.show({
              text: "Error selecting competitor. Please try again.",
              color: "error"
            });
          }
        });
      } catch (error) {
        console.error("Error initializing Google Places Autocomplete:", error);
      }
    },
  },

  async mounted() {
    if (this.initialLocationId) {
      // Wait for stores to load if needed using a watcher, then preSelectLocation
      if (!this.stores || this.stores.length === 0) {
        const unwatch = this.$watch('stores', (newStores) => {
          if (newStores && newStores.length > 0) {
            this.preSelectLocation(this.initialLocationId);
            unwatch(); // Stop watching once stores are loaded and preselection is attempted
          }
        }, { immediate: true }); // immediate might attempt too early if stores are async, but once: true in preSelectLocation helps
      } else {
        this.preSelectLocation(this.initialLocationId);
      }
    } else if (this.stores?.length > 0) {
      await this.selectStore(this.stores[0]); // Select the first store and ensure it completes
      // After selecting the first store, if there's a URL keyword, try to apply it
      if (this.urlKeyword && this.keywordsWithRanks && this.keywordsWithRanks.length > 0) {
        const matchingRankedKeyword = this.keywordsWithRanks.find(kw => 
          kw.keyword.toLowerCase() === this.urlKeyword.toLowerCase()
        );
        if (matchingRankedKeyword) {
          this.handleKeywordChange(matchingRankedKeyword.keyword);
        } else if (this.keywordsWithRanks.length > 0) {
          this.handleKeywordChange(this.keywordsWithRanks[0].keyword); // Fallback to first if URL keyword not found
        }
      } else if (this.keywordsWithRanks && this.keywordsWithRanks.length > 0) {
        this.handleKeywordChange(this.keywordsWithRanks[0].keyword); // Default to first keyword if no URL keyword
      }
    }
    
    document.addEventListener("click", this.handleClickOutside);
    
    // Initialize Google Places Autocomplete
    this.$nextTick(() => {
      const checkGoogleMapsLoaded = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          console.log("Google Maps Places API loaded, initializing autocomplete");
          this.initAutocomplete();
        } else {
          console.log("Waiting for Google Maps Places API to load...");
          setTimeout(checkGoogleMapsLoaded, 500);
        }
      };
      checkGoogleMapsLoaded();
    });
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.text-xs {
  font-size: 0.75rem;
}

:deep(.v-text-field.location-input) {
  .v-input__slot {
    min-height: 40px !important;
  }

  .v-text-field__placeholder {
    color: rgba(0, 0, 0, 0.6);
    font-size: 14px;
  }

  input {
    font-size: 14px;
  }

  fieldset {
    border-color: rgba(0, 0, 0, 0.6) !important;
  }
}

.text-center {
  text-align: center;
}

img {
  object-fit: cover;
}

.cursor-pointer {
  cursor: pointer;
}

.hover\:bg-gray-100:hover {
  background-color: #f7fafc;
}

.leading-tight {
  line-height: 1.25;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

.skeleton-loader {
  display: inline-block;
  width: 50px;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 4px;
  animation: skeleton-loading 1.5s infinite;
}

.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

/* Optional: Custom scrollbar styling */
.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.tooltip-container {
  position: relative;
  z-index: 40;
}

.v-tooltip__content {
  z-index: 60 !important;
  position: fixed !important;
}

.v-icon {
  z-index: 40;
  position: relative;
}

.showStoreDropdown {
  background-color: white;
  /* Ensure solid background */
  z-index: 50;
  position: relative;
}

.date-select {
  position: relative;
  z-index: 40;
}

button.tabs {
  border-width: 2px;
  border-style: solid;
  border-color: #8056de;
}

button.tabs.active {
  background-color: #8056de;
  color: white;
}

.metrics-select {
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

/* This ensures the v-select dropdown menu appears above everything */
:deep(.v-menu__content) {
  z-index: 60 !important;
}

.report-container {
  font-family: Arial, sans-serif;
  padding: 20px;
}

.overview {
  margin-top: 20px;
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 5px;
}

.hide-panel {
  background-color: #f0f0f0;
  border: none;
  padding: 5px 10px;
  cursor: pointer;
  float: right;
}

.stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin-top: 10px;
}

.stat-item {
  background-color: #fff;
  padding: 10px;
  border-radius: 5px;
  text-align: center;
  flex: 1 1 45%;
  margin: 5px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
}

.value {
  display: flex;
  justify-content: center;
  align-items: center;
}

.up {
  color: green;
  margin-left: 5px;
}

.down {
  color: red;
}

.results {
  margin-top: 30px;
}

.select-keyword {
  /* background-color: #4caf50; */
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

.keyword-menu {
  z-index: 1000;
  /* Ensure the menu is above other elements */
}

.keyword-name {
  flex: 1;
  /* Allow the keyword name to take available space */
  text-align: left;
  /* Align text to the left */
}

.avg-rank,
.change {
  margin-left: 10px;
  /* Add some space between elements */
  min-width: 50px;
  /* Ensure a minimum width for alignment */
  text-align: right;
  /* Align text to the right */
}

.header-row {
  border-bottom: 1px solid #e0e0e0;
  /* Add a bottom border for separation */
  padding-bottom: 5px;
  /* Add some padding */
}

.keyword-header {
  flex: 1;
  text-align: left;
}

.keyword-name {
  flex: 1;
  text-align: left;
}

.search-volume,
.avg-rank {
  flex: 1;
  text-align: right;
}

.scrollable-list {
  overflow-x: auto;
  /* Enable horizontal scrolling */
  white-space: nowrap;
  /* Prevent line breaks */
}

/* Timeline Styles */
.timeline {
  display: flex;
  justify-content: space-between;
  width: 100%;
  overflow-x: auto;
  /* allow horizontal scrolling */
  margin-top: 20px;
}

/* The inner wrapper expands to fit all timeline-point items. */
.timeline-inner {
  position: relative;
  width: 100%;
  /* inline-block ensures its width = total width of children, 
     so the line can match the full width of the points. */
}

/* Absolutely position the line inside timeline-inner so it stretches 
   behind all points, not just across the visible container. */
.timeline-line {
  position: absolute;
  top: 18px;
  /* adjust as needed */
  left: 0;
  height: 2px;
  width: 100%;
  background-color: #ccc;
  z-index: 1;
  /* sit behind the points */
}

.timeline-points {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 2;
  padding: 10px 0;
  width: 100%;
}

.timeline-point {
  flex: 1;
  text-align: center;
  position: relative;
  z-index: 2;
}

.circle {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: #4caf50;
  /* default */
  margin: 0 auto;
  /* center horizontally */
}

.circle.orange {
  background-color: #ff9800;
}

.date {
  margin-top: 5px;
  white-space: nowrap;
  /* prevent date from wrapping */
}

.tooltip {
  position: absolute;
  background-color: #fff;
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 4px;
  z-index: 1000;
  white-space: nowrap;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transform: translate(120%, -120%);
  /* Position above the point */
  margin-top: -10px;
}

.map-container {
  width: 100%;
  height: 400px;
  /* Adjust height as per your requirement */
  max-width: 100%;
  overflow: hidden;
  border-radius: 8px;
  /* Optional for rounded corners */
}

.map-wrapper {
  width: 100%;
  overflow: hidden;
}

.grid-menu {
  background-color: white;
  /* Solid white background */
}

.v-menu__content {
  background-color: white !important;
  /* Set solid white background */
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  /* Optional: add shadow for depth */
}

.selected .circle-parent .circle-marker {
  border: 2px solid blue !important;
  /* Border color for selected grid */
  border-radius: 50%;
  /* Optional: rounded corners */
  padding: 5px;
  /* Optional: padding for better visibility */
}

.marker-circle {
  width: 40px;
  height: 40px;
  background-color: red;
  border-radius: 50%;
  border: 2px solid transparent;
  position: relative;
}

.marker-circle.selected {
  border-color: blue;
}

/* Improved styles */
.location-input :deep(.v-input__slot) {
  min-height: 40px !important;
  border-radius: 8px;
}

.location-input :deep(.v-text-field__placeholder) {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}

.location-input :deep(input) {
  font-size: 14px;
}

.location-input :deep(fieldset) {
  border-color: rgba(0, 0, 0, 0.2) !important;
  transition: border-color 0.2s;
}

.location-input:hover :deep(fieldset) {
  border-color: rgba(0, 0, 0, 0.4) !important; 
}

/* Enhanced dropdown styling */
[ref="showStoreDropdown"] {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.1);
  animation: fadeInDown 0.2s ease-out;
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

[ref="showStoreDropdown"] > div {
  margin-bottom: 2px;
  border-left: 2px solid transparent;
}

[ref="showStoreDropdown"] > div:hover {
  border-left-color: #3b82f6;
}

[ref="showStoreDropdown"] > div.bg-blue-50 {
  background-color: rgba(59, 130, 246, 0.08);
}

.select-keyword {
  background-color: white;
  transition: all 0.2s;
  border: 1px solid #d1d5db;
  height: 40px;
}

.select-keyword:hover:not(:disabled) {
  border-color: #a0aec0;
  background-color: #f9fafb;
}

:deep(.v-menu__content) {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.map-container {
  border-radius: 0;
  overflow: hidden;
}

/* Make the map controls more visible */
:deep(.gm-style-mtc button),
:deep(.gm-style-mtc div),
:deep(.gm-style .gm-style-cc) {
  font-size: 12px !important;
}

/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c5c5c5;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}
</style>
